import {
  howItIsHeld,
  NEEDS_AT_LEAST,
  TAKES_AT_MOST,
  whatIsWrongSoFar,
  whatIsWrongWith,
  whoMade,
} from "@rally/agreed";
import {
  Alert,
  Button,
  Col,
  Field,
  Input,
  ShimmerText,
  Stack,
  Text,
  useTheme,
  type ViewStyle,
} from "@rally/ui-kit";
import { useEffect, useState } from "react";
import { KeyRow } from "../keys/key-row.tsx";
import { say } from "../localization/say.ts";
import { addAPasskey, beginRegistering, type Registering, read, registerWith } from "../net/rally.ts";
import { why } from "../net/why.ts";
import { Doorway } from "./doorway.tsx";

type WhatTheLinkSays = {
  invitedBy: string | null;
  live: boolean;
  role: string | null;
  project: string | null;
};

function asAWord(role: string): string {
  return role === "owner"
    ? say("invite.role.owner").toLowerCase()
    : role === "admin"
      ? say("invite.role.admin").toLowerCase()
      : say("invite.role.member").toLowerCase();
}

export function Register({ invite, onIn }: { invite: string; onIn: () => void }) {
  const theme = useTheme();
  const [handle, setHandle] = useState("");
  const [touched, setTouched] = useState(false);
  const [going, setGoing] = useState<Registering | null>(null);
  const [made, setMade] = useState<number>(0);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const [live, setLive] = useState<boolean | undefined>(undefined);
  const [by, setBy] = useState<string | null>(null);

  const [joining, setJoining] = useState<{ role: string | null; project: string | null }>({
    role: null,
    project: null,
  });
  const [taken, setTaken] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [names, setNames] = useState<string[]>([]);
  const [held, setHeld] = useState<(string | null)[]>([]);

  const [called, setCalled] = useState<string[]>([]);
  const [opening, setOpening] = useState(false);
  const [openingWrong, setOpeningWrong] = useState<string | null>(null);

  useEffect(() => {
    read<WhatTheLinkSays>("invite.who", { invite })
      .then((said) => {
        setLive(said.live);
        setBy(said.invitedBy);
        setJoining({ role: said.role, project: said.project });
      })
      .catch(() => setLive(true));
  }, [invite]);

  const wanted = handle.trim();
  const wrong = whatIsWrongWith(wanted);
  const shaped = wrong === null;

  const alreadyWrong = whatIsWrongSoFar(wanted);

  useEffect(() => {
    if (!shaped || made > 0) return;
    setAsking(true);
    const wait = setTimeout(() => {
      read<{ handle: string; free: boolean }>("handle.free", { invite, handle: wanted })
        .then((said) => {
          if (said.handle !== wanted) return;
          setTaken(said.free ? null : wanted);
        })
        .catch(() => {})
        .finally(() => setAsking(false));
    }, 300);
    return () => {
      clearTimeout(wait);
      setAsking(false);
    };
  }, [wanted, shaped, invite, made]);

  const isTaken = taken !== null && taken === wanted;

  const free = shaped && !isTaken && !asking && wanted.length > 0;

  async function addOne() {
    setBusy(true);
    setRefused(null);
    try {
      const going2 = going ?? beginRegistering(wanted);
      await addAPasskey(going2);
      setGoing(going2);
      setMade(going2.devices.length);

      setNames(going2.devices.map((device) => whoMade(device)));
      setHeld(going2.devices.map((device) => howItIsHeld(device)));
    } catch (cause) {
      setRefused(why(cause, "add"));
    } finally {
      setBusy(false);
    }
  }

  function forget(at: number) {
    if (!going) return;
    going.devices.splice(at, 1);
    setMade(going.devices.length);
    setNames(going.devices.map((device) => whoMade(device)));
    setHeld(going.devices.map((device) => howItIsHeld(device)));
    setCalled(called.filter((_, index) => index !== at).concat(""));
    setRefused(null);
  }

  async function open() {
    if (!going || going.devices.length < 2) return;
    setOpening(true);
    setOpeningWrong(null);
    try {
      await registerWith(invite, going, called);
      onIn();
    } catch (cause) {
      setOpeningWrong(why(cause, "add"));
    } finally {
      setOpening(false);
    }
  }

  const slots = Array.from(
    { length: Math.min(Math.max(NEEDS_AT_LEAST, made + 1), TAKES_AT_MOST) },
    (_, at) => at,
  );

  if (live === false) {
    return (
      <Doorway title={say("door.spent")}>
        <Alert tone="warning">{say("join.spent")}</Alert>
      </Doorway>
    );
  }

  const complaint = alreadyWrong ?? (isTaken ? say("join.handleTaken") : touched && !shaped ? wrong : null);
  const under = complaint ? (
    <Text typeName="caption" color="danger" accessibilityLiveRegion="polite">
      {complaint}
    </Text>
  ) : asking && shaped ? (
    <ShimmerText>{say("join.handleChecking")}</ShimmerText>
  ) : free ? (
    <ShimmerText color="accent" highlight="accentStrong" ms={3200}>
      {say("join.handleYours")(wanted)}
    </ShimmerText>
  ) : (
    <Text typeName="caption" color="text.tertiary">
      {say("join.handleRule")}
    </Text>
  );

  return (
    <Doorway title={say("door.join")}>
      {by ? (
        <Text typeName="body" color="text.secondary">
          {say("join.invitedBy")(by)}
        </Text>
      ) : null}
      {joining.role ? (
        <Text typeName="bodySemibold" color="text.primary" testID="joining">
          {say("join.what")(asAWord(joining.role), joining.project ?? say("join.thisServer"))}
        </Text>
      ) : null}
      {joining.role === "owner" ? <Alert tone="warning">{say("join.ownerWarning")}</Alert> : null}
      <Field label={say("join.handle")} htmlFor="handle" hint={under}>
        <Input
          id="handle"
          testID="handle"
          value={handle}
          onChangeText={(said) => setHandle(said.toLowerCase())}
          onBlur={() => setTouched(true)}
          placeholder={say("join.handlePlaceholder")}
          editable={made === 0}
          settled={made > 0}
          invalid={complaint !== null}
        />
      </Field>

      <Col
        aria-disabled={!free}
        pointerEvents={free ? "auto" : "none"}
        style={
          {
            marginTop: theme.space[6],
            gap: theme.space[3],
            opacity: free ? 1 : 0.4,
            transitionProperty: "opacity",
            transitionDuration: theme.preferReduced ? "0ms" : "200ms",
          } as ViewStyle
        }
      >
        <Stack gap={1}>
          <Text typeName="label" color="text.secondary">
            {say("join.keys")}
          </Text>
          <Text typeName="caption" color="text.tertiary">
            {say("join.keysWhy")}
          </Text>
        </Stack>
        <Stack gap={2}>
          {slots.map((at) => (
            <KeyRow
              key={at}
              testID={`key-${at + 1}`}
              done={made > at}
              waiting={made === at && busy}
              ready={free && made === at}
              label={names[at] ?? say(at === 0 ? "join.keyOne" : at === 1 ? "join.keyTwo" : "join.keyMore")}
              hint={
                held[at] ??
                say(at === 0 ? "join.keyOneWhere" : at === 1 ? "join.keyTwoWhere" : "join.keyMoreWhere")
              }
              wrong={made === at ? refused : null}
              spare={at >= NEEDS_AT_LEAST}
              called={called[at] ?? ""}
              onCalled={(said) => setCalled(called.map((was, index) => (index === at ? said : was)))}
              onRemove={() => forget(at)}
              onAdd={addOne}
            />
          ))}
        </Stack>
      </Col>

      <Col style={{ marginTop: theme.space[6], gap: theme.space[2] }}>
        <Button
          variant="primary"
          block
          disabled={made < 2 || opening}
          loading={opening}
          onPress={open}
          testID="open-account"
        >
          {say("join.open")(wanted || "")}
        </Button>
        {openingWrong ? (
          <Text typeName="caption" color="danger" accessibilityLiveRegion="polite">
            {openingWrong}
          </Text>
        ) : null}
      </Col>
    </Doorway>
  );
}
